import React, { useEffect, useState } from "react";
import {
  Row, Col, Card, Switch, Button, notification, Typography, Spin, Divider,
} from "antd";
import {
  SettingOutlined, SaveOutlined, CameraOutlined, CalendarOutlined,
  PhoneOutlined, MailOutlined, UserOutlined, GlobalOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { baseUrl } from "../../config";

const { Title, Text } = Typography;

const CONFIG_FIELDS = [
  { key: 'show_profile_photo',   label: 'Profile Photo Upload',   sub: 'सदस्य की प्रोफाइल फोटो अपलोड करने का option',   icon: <CameraOutlined />,   color: '#7a0000' },
  { key: 'show_business_photo',  label: 'Business Photo Upload',  sub: 'दुकान/फर्म की फोटो अपलोड करने का option',        icon: <CameraOutlined />,   color: '#7a0000' },
  { key: 'show_dob',             label: 'Date of Birth (जन्म तिथि)', sub: 'जन्म तिथि दर्ज करने का field',               icon: <CalendarOutlined />, color: '#7a0000' },
  { key: 'show_gotra',           label: 'Gotra (गोत्र)',          sub: 'गोत्र दर्ज करने का field',                       icon: <UserOutlined />,     color: '#7a0000' },
  { key: 'show_email',           label: 'Email Address (ईमेल)',   sub: 'ईमेल address दर्ज करने का field',                icon: <MailOutlined />,     color: '#7a0000' },
  { key: 'show_alternate_phone', label: 'Alternate Phone (वैकल्पिक नंबर)', sub: 'दूसरा मोबाइल नंबर दर्ज करने का field', icon: <PhoneOutlined />,    color: '#7a0000' },
  { key: 'show_current_city',    label: 'Current City (वर्तमान शहर)', sub: 'वर्तमान निवास शहर का field',               icon: <GlobalOutlined />,   color: '#7a0000' },
];

function DirectoryConfig() {
  const [config, setConfig] = useState({});
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/business-directory/config`, { headers: authHeader() });
      if (res.data.status) {
        const map = {};
        (res.data.result || []).forEach(r => { map[r.config_key] = r.config_value === '1'; });
        setConfig(map);
      }
    } catch {
      notification.error({ message: 'Error', description: 'Could not load config', placement: 'topRight' });
    } finally {
      setLoading(false);
    }
  };

  const onToggle = (key, val) => {
    setConfig(prev => ({ ...prev, [key]: val }));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(config).forEach(([k, v]) => { payload[k] = v ? '1' : '0'; });
      await axios.patch(`${baseUrl}/business-directory/config`, payload, { headers: authHeader() });
      notification.success({ message: 'Saved', description: 'Public form config updated successfully.', placement: 'topRight' });
    } catch (err) {
      notification.error({ message: 'Error', description: err?.response?.data?.message || 'Save failed', placement: 'topRight' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs={24} lg={16}>
          <Card
            bordered={false}
            className="criclebox mb-24"
            title={
              <span style={{ color: 'white', fontWeight: 600, fontSize: 16 }}>
                <SettingOutlined style={{ marginRight: 8 }} />
                Business Directory — Public Form Settings
              </span>
            }
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0', borderRadius: '8px 8px 0 0'}}
            extra={
              <Button
                icon={<SaveOutlined />}
                loading={saving}
                onClick={onSave}
                style={{ background: '#ffffff', borderColor: '#ffffff', color: '#7a0000', borderRadius: 8, fontWeight: 700 }}
              >
                Save Changes
              </Button>
            }
          >
            <Spin spinning={loading}>
              <div style={{ padding: '8px 24px 24px' }}>

                <div style={{ background: '#f0f7ff', border: '1px solid #d6e4ff', borderRadius: 8, padding: '12px 16px', marginBottom: 24 }}>
                  <Text style={{ fontSize: 13, color: '#7a0000' }}>
                    <strong>Note:</strong> These toggles control which optional fields appear in the <strong>public form</strong> (/business-directory-form).
                    All these fields are always available in the admin "Add Entry" drawer.
                  </Text>
                </div>

                <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '1.2px', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
                  Optional Fields — Show in Public Form?
                </div>

                {CONFIG_FIELDS.map((field, i) => (
                  <div key={field.key}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 8, background: config[field.key] ? field.color : '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}>
                          <span style={{ color: config[field.key] ? '#fff' : '#aaa', fontSize: 16 }}>{field.icon}</span>
                        </div>
                        <div>
                          <Text strong style={{ fontSize: 14, display: 'block' }}>{field.label}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>{field.sub}</Text>
                        </div>
                      </div>
                      <Switch
                        checked={!!config[field.key]}
                        onChange={val => onToggle(field.key, val)}
                        checkedChildren="ON"
                        unCheckedChildren="OFF"
                      />
                    </div>
                    {i < CONFIG_FIELDS.length - 1 && <Divider style={{ margin: 0 }} />}
                  </div>
                ))}

                <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saving}
                    onClick={onSave}
                    size="large"
                    style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 8, fontWeight: 600, minWidth: 160 }}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            </Spin>
          </Card>
        </Col>

        {/* Info card */}
        <Col xs={24} lg={8}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <Title level={5} style={{ color: '#7a0000', marginBottom: 12 }}>How it works</Title>
            <div style={{ fontSize: 13, color: '#555', lineHeight: 1.8 }}>
              <p>✅ <strong>ON</strong> — Field shows in public form that community members fill.</p>
              <p>❌ <strong>OFF</strong> — Field is hidden from public form (admin can still fill it from panel).</p>
              <Divider />
              <p style={{ color: '#7a0000' }}>📸 <strong>Photo fields:</strong> When enabled, public form shows an upload button. Max size: <strong>500KB</strong>.</p>
              <p style={{ color: '#7a0000' }}>🎂 <strong>Birthday:</strong> When enabled, can be used for birthday wishes automation.</p>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default DirectoryConfig;
